import { IFieldData } from '../../models/application-form-data.model';
import { IIremboFileUpload } from '../../models/irembo-file-upload-response.model';
import { IIrembogovBasicLabelKeyPair } from '../../models/irembogov-basic-label-keypair.model';

export type TSingleLineValue = string | string[] | null;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const fieldCardUtilTrackByFn = (index: number) => {
  return index;
};

// Complex field types that should not be processed by getSingleLineValue
const complexFieldTypes: string[] = [
  'customdatatable',
  'customitemselectiontable',
  'custommultiselectdatafetch',
  'customtablewithinputform',
];

export const fieldCardUtilGetSingleLineValue = (
  field: IFieldData | undefined,
  multiLineValueFieldTypes: string[]
): TSingleLineValue => {
  if (!field || multiLineValueFieldTypes.indexOf(field.fieldType) > -1) {
    return null;
  }

  // Skip complex field types that have their own rendering
  if (complexFieldTypes.includes(field.fieldType)) {
    return null;
  }

  let valueString: TSingleLineValue = '';
  switch (field.fieldType) {
    case 'customdropdown':
    case 'customdropdownpaginated': {
      valueString = fieldCardUtilGetValueForCustomDropDowns(field);
      break;
    }
    case 'multicheckbox': {
      valueString = fieldCardUtilGetValueForMulticheckbox(field);
      break;
    }
    case 'customdoubleidinput': {
      if (field.fieldValue) {
        valueString = <string>(
          (<Record<string, unknown>>field.fieldValue)['idValue']
        );
        break;
      }
      valueString = null;
      break;
    }
    default: {
      if (field.fieldValue) {
        valueString = <string>field.fieldValue;
        break;
      }
      valueString = null;
    }
  }

  return valueString;
};

export const fieldCardUtilGetMultiLineValues = (
  field: IFieldData | undefined,
  multiLineValueFieldTypes: string[]
): IIrembogovBasicLabelKeyPair<string | null>[] => {
  const values: IIrembogovBasicLabelKeyPair<string | null>[] = [];
  if (!field || multiLineValueFieldTypes.indexOf(field?.fieldType) < 0) {
    return values;
  }
  if (field.fieldType === 'customdoubleidinput') {
    if (field.fieldValue && typeof field.fieldValue === 'object') {
      const countryValue: unknown = (
        field.fieldValue as Record<string, unknown>
      )['idCountry'];
      values.push(
        {
          key: <string>(<Record<string, unknown>>field.fieldValue)['idValue'],
          label: `${field?.fieldLabel} ID Number`,
        },
        {
          key: <string>(<Record<string, unknown>>field.fieldValue)['idType'],
          label: `${field?.fieldLabel} ID Type`,
        },
        {
          key: (countryValue as Record<string, unknown>)['label'] as string,
          label: `${field?.fieldLabel} Country`,
        }
      );
    }
  }
  return values;
};

export const fieldCardUtilGetValueForMulticheckbox = (
  field: IFieldData | undefined
): TSingleLineValue => {
  if (!field) return null;
  let fieldValueString: TSingleLineValue = '';
  const fieldValue: unknown = field.fieldValue;

  if (Array.isArray(fieldValue)) {
    fieldValueString = fieldValue.join(', ');
    return fieldValueString;
  }
  if (!Array.isArray(fieldValue)) {
    if (typeof fieldValue === 'object') {
      const checkBoxValueObj: Record<string, boolean> = <
        Record<string, boolean>
      >fieldValue;
      fieldValueString = Object.keys(checkBoxValueObj).filter(
        key => checkBoxValueObj[key]
      );
    } else {
      fieldValueString = <string>fieldValue;
    }
    return fieldValueString;
  }
  return fieldValueString;
};

export const fieldCardUtilGetValueForCustomDropDowns = (
  field: IFieldData | undefined
): TSingleLineValue => {
  if (!field) return null;
  let valueString: TSingleLineValue = '';
  if (!Array.isArray(field.fieldValue)) {
    if (typeof field.fieldValue === 'object') {
      valueString = (<Record<string, string>>field.fieldValue)[
        <string>field.fieldBindLabel
      ];
    } else {
      valueString = <string>field.fieldValue;
    }
    return valueString;
  }

  if (Array.isArray(field.fieldValue)) {
    const arrObj: Record<string, unknown>[] = field.fieldValue;
    if (arrObj.every(elem => typeof elem === 'object')) {
      valueString = arrObj.map((ele: Record<string, unknown>) => {
        return ele[field.fieldBindLabel as string] as string;
      });
    } else {
      valueString = arrObj.map(x => String(x));
    }
    return valueString;
  }
  return valueString;
};

export const fieldCardUtilIsImage = (mimeType: unknown) => {
  return (mimeType as string).split('/')[0].toLowerCase() === 'image';
};

export const fieldCardUtilGetImagePath = (file: unknown): string => {
  const iremboFileUpload = file as IIremboFileUpload;
  return iremboFileUpload.content as string;
};

export const fieldCardUtilDownloadFile = (
  base64String: string,
  fileName: string
) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = base64String;
  downloadLink.download = fileName;
  downloadLink.click();
};

// External file upload utilities
export const fieldCardUtilGetExternalFileDetails = (
  fieldValue: unknown
): {
  fileName: string;
  fileSize: string;
  fileType: string;
  url?: string;
} => {
  const file = fieldValue as any;

  if (!file) {
    return {
      fileName: 'No file',
      fileSize: 'Unknown',
      fileType: 'Unknown',
    };
  }

  return {
    fileName: file.fileName || 'External File',
    fileSize: file.fileSize ? formatFileSize(file.fileSize) : 'Unknown size',
    fileType: file.fileType || 'Unknown type',
    url:
      file.uploadResponse?.url ||
      file.fileDownloadUrl ||
      file.url ||
      file.downloadUrl ||
      file.fileUrl,
  };
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
